/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/class/TimeSpan.ts
 * @create:      2023-11-27 11:29:26.634
 * @modify:      2025-07-01 13:13:47.373
 * @version:     6.0.0
 * @times:       23
 * @lines:       189
 * @copyright:   Copyright © 2023-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { ITimeSpan } from "../interface/ITimeSpan";
import { ToDays } from "../number/ToDays";
import { ToHours } from "../number/ToHours";
import { ToMilliseconds } from "../number/ToMilliseconds";
import { ToMinutes } from "../number/ToMinutes";
import { ToSeconds } from "../number/ToSeconds";
import { ToWeeks } from "../number/ToWeeks";

/**
 * Represents a duration of time and provides methods to access
 * individual components of the duration (milliseconds, seconds, minutes, etc.).
 * 
 * @since 4.3.0
 * @version 2023-11-27
 */
export class TimeSpan {
    /**
     * Total duration in milliseconds.
     */
    private totalMilliseconds: number;

    /**
     * Constructs a TimeSpan object.
     * @param milliseconds - The total duration in milliseconds.
     */
    public constructor(milliseconds: number);

    /**
     * Constructs a TimeSpan object from an existing ITimeSpan object.
     * @param span - An ITimeSpan object containing the duration.
     */
    public constructor(span: ITimeSpan);

    /**
     * Constructs a TimeSpan object from a numeric value or an ITimeSpan object.
     * @param value - The total duration in milliseconds or an ITimeSpan object.
     */
    public constructor(value: number | ITimeSpan) {
        if (typeof value === "number") {
            this.totalMilliseconds = value;
        } else {
            this.totalMilliseconds = ToMilliseconds(value);
        }
    }

    /**
     * Gets the remaining milliseconds after extracting whole seconds.
     */
    public get Milliseconds() {
        const remainingMilliseconds = this.totalMilliseconds % 1000;
        return remainingMilliseconds;
    }

    /**
     * Gets the whole seconds in the duration.
     */
    public get Seconds() {
        const seconds = Math.floor(this.TotalSeconds % 60);
        return seconds;
    }

    /**
     * Gets the whole minutes in the duration.
     */
    public get Minutes() {
        const minutes = Math.floor(this.TotalMinutes % 60);
        return minutes;
    }

    /**
     * Gets the whole hours in the duration.
     */
    public get Hours() {
        const hours = Math.floor(this.TotalHours % 24);
        return hours;
    }

    /**
     * Gets the whole days in the duration.
     */
    public get Days() {
        const days = Math.floor(this.TotalDays % 7);
        return days;
    }

    /**
     * Gets the whole weeks in the duration.
     */
    public get Weeks() {
        const weeks = Math.floor(this.TotalWeeks);
        return weeks;
    }

    /**
     * Gets the TimeSpan representation of the duration, breaking down
     * the total duration into individual components.
     * @returns An ITimeSpan object representing the duration.
     */
    public get Span(): ITimeSpan {
        return {
            milliseconds: this.Milliseconds,
            seconds: this.Seconds,
            minutes: this.Minutes,
            hours: this.Hours,
            days: this.Days,
            weeks: this.Weeks,
        };
    }

    /**
     * Gets the total duration in milliseconds.
     */
    public get TotalMilliseconds() {
        return this.totalMilliseconds;
    }

    /**
     * Gets the total duration in whole seconds.
     */
    public get TotalSeconds() {
        const seconds = ToSeconds(this.totalMilliseconds, "milliseconds");
        return seconds;
    }

    /**
     * Gets the total duration in whole minutes.
     */
    public get TotalMinutes() {
        const minutes = ToMinutes(this.totalMilliseconds, "milliseconds");
        return minutes;
    }

    /**
     * Gets the total duration in whole hours.
     */
    public get TotalHours() {
        const hours = ToHours(this.totalMilliseconds, "milliseconds");
        return hours;
    }

    /**
     * Gets the total duration in whole days.
     */
    public get TotalDays() {
        const days = ToDays(this.totalMilliseconds, "milliseconds");
        return days;
    }

    /**
     * Gets the total duration in whole weeks.
     */
    public get TotalWeeks() {
        const weeks = ToWeeks(this.totalMilliseconds, "milliseconds");
        return weeks;
    }
}
